import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  RemortgageTimeline,
  Typography,
  calculateRemortgageTimelineVariantAndMonths,
  isMonthSupported,
} from "@trussle/external-components";
import { Graphql } from "../../apollo";
import { PanelWrapper } from "../../PanelWrapper";
import { useContactId, useGetLoggedInStatus } from "../../LoggedInStatus";
import { useInitialValues } from "./useSetInitialValues";
import { useGetOnSubmit } from "./onSubmit";
import { HubSpotForm } from "../../HubSpot/HubSpotForm";
import { useDebounceRepayment, useDebounceValues } from "./useDebounceValues";
import { useHubspotPopulateFields } from "./useHubspotPopulateFields";

import "./styles.scss";
import { InformationPanel } from "./informationPanel";
import { getDateObject } from "./date";
import { Infogram } from "./infogram";
import { Inputs } from "./inputs";

export interface IRateWatchPanelFormValues {
  repayment: string | null;
  balance: string | null;
  endDate: Date | string | null;
  propertyValue: string | null;
  currentTerm: number | null;
}

export const RateWatchPanelWithoutApollo: React.FC = () => {
  const status = useGetLoggedInStatus();
  const [cheapestRepayment, setCheapestRepayment] = useState({
    initialPeriodRate: 0,
    monthlyRepayment: 0,
  });
  const [cheapestRepaymentLoading, setCheapestRepaymentLoading] = useState(false);
  const contactId = useContactId();
  const [monthSupported, setMonthSupported] = useState<undefined | boolean>(
    undefined,
  );
  const {
    register, watch, setValue, reset, control,
  } = useForm<IRateWatchPanelFormValues>();

  useEffect(() => {
    setMonthSupported(isMonthSupported());
  }, [setMonthSupported, isMonthSupported]);

  const { isLoading, applicationId } = useInitialValues(
    contactId,
    status,
    reset,
    setValue,
    monthSupported,
  );
  const onSubmit = useGetOnSubmit(contactId, status, applicationId);

  const endDateMethods = register("endDate", { required: true });
  const repaymentValue = watch("repayment");
  const propertyValue = watch("propertyValue");
  const balance = watch("balance");
  const endDate = watch("endDate");
  const endDateValue = getDateObject(endDate?.toString(), monthSupported);
  const shouldShowLoading = isLoading || !repaymentValue || !propertyValue || !balance || !endDate;
  const { monthsUntilRemo, variant: timelineVariant } = calculateRemortgageTimelineVariantAndMonths(endDateValue, shouldShowLoading);
  const onBeforeFormSubmit = useHubspotPopulateFields(watch, endDateValue);
  useDebounceRepayment(
    watch,
    setCheapestRepayment,
    setCheapestRepaymentLoading,
    timelineVariant,
  );
  useDebounceValues(watch, contactId, status, endDateValue, isLoading);
  return (
    <>
      <PanelWrapper className="rate-watch-panel__top-section">
        <Typography variant="h1" classes="rate-watch-panel__title">
          Mortgage RateWatch
        </Typography>
        <p className="rate-watch-panel__subtitles">
          Your free remortgage rate-monitoring service. Ensure you're on the best rate possible by locking in six-months out.
          We'll track rates and reach out if a more suitable one becomes available before your current term ends.
        </p>
        <form>
          <Inputs
            endDate={endDate as Date}
            monthSupported={monthSupported}
            endDateMethods={endDateMethods}
            monthsUntilRemo={monthsUntilRemo}
            setValue={setValue}
            control={control}
            loading={isLoading}
          />
        </form>
      </PanelWrapper>
      <PanelWrapper
        backgroundColour="light-grey"
        className="rate-watch-panel__bottom-section"
      >
        <div className="rate-watch-panel__bottom-section__results">
          {shouldShowLoading ? (
            <>
              <Typography variant="h2" classes="rate-watch-panel__title">
                Your Remortgage Timeline
              </Typography>
              <Typography id="typography_subtitle" variant="body1">
                Fill in your details above to generate a custom timeline for
                your upcoming remortgage.
              </Typography>
              <Typography
                variant="body1"
                classes="rate-watch-panel__title rate-watch-panel__bottom-section__timeline--prompt"
              >
                Your Timeline to Remortgage:
              </Typography>
              <RemortgageTimeline
                isPreview
                onClick={() => null}
                remortgageDate={new Date()}
                classOverrides={{
                  timeline: "rate-watch-panel__bottom-section__timeline",
                }}
              />
            </>
          ) : (
            <>
              <InformationPanel
                cheapestRepayment={cheapestRepayment}
                cheapestRepaymentLoading={cheapestRepaymentLoading}
                variant={timelineVariant}
                control={control}
              />
              <RemortgageTimeline
                remortgageDate={endDateValue}
                onClick={onSubmit}
                classOverrides={{
                  timeline: "rate-watch-panel__bottom-section__timeline",
                }}
              >
                <HubSpotForm
                  hubSpotFormId="44627b0e-742e-4c43-a8bb-f499c6195f0a"
                  hubSpotPortalId="6280838"
                  index="44627b0e-742e-4c43-a8bb-f499c6195f0a"
                  onBeforeFormSubmit={onBeforeFormSubmit}
                  numberOfQuestions={1}
                />
              </RemortgageTimeline>
            </>
          )}
          <Infogram />
        </div>
      </PanelWrapper>
    </>
  );
};

export const RateWatchPanel = () => (
  <Graphql>
    <RateWatchPanelWithoutApollo />
  </Graphql>
);
